function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import camelCase from 'lodash/camelCase';
import { VirtualElement } from '@floating-ui/react';
import { clsx } from 'clsx';
import { createPortal } from 'react-dom';
import { useTranslation } from 'common/i18n/hooks';
import { colors } from 'common/util/colors';
import { TEXT_HIGHLIGHT_THEME, TextHighlightReact } from 'common/components/text-highlight';
import { Directions, PositioningStrategy, PositionParentType } from '../drop-menu/types';
import { SvgIconReact } from '../svg-icon';
import { DropMenuReact } from '../drop-menu';
import { TooltipBulletStyle, TooltipSection } from './types';
import css from './index.module.styl?inline';
import { TooltipItem, TooltipItemType } from './types';
import styles from './index.module.styl';
export type ChartTooltipProps = {
  /** Hacky way to pass down css module styles from a React parent using css
   * modules. This is a stop-gap till all the parents are converted to React.*/
  cssModule?: any;
  className?: string;
  children?: React.ReactNode;
  direction?: Directions;
  footerText?: string;
  offset?: number;
  // When provided, the tooltip will automatically position itself relative to this element using floating-ui.
  // See mp-drop-menu for details.
  positioning?: ChartTooltipPositioning;
  /** This requires `positioning` to be set. Also note, portal won't work with
   * `positioning={{positionParent: PositionParentType.ParentElement}}`. Must pass an element
   * instead or use `MousePositioning`. */
  renderInPortal?: boolean;
  sections?: TooltipSection[];
  titleColor?: string;
  pointerEvents?: React.CSSProperties[`pointerEvents`];
};
type MousePositioning = {
  clientX: number;
  clientY: number;
};
type ParentPositioning = {
  positionParent: HTMLElement | PositionParentType.ParentElement;
};

// can either position automatically relative to an element or absolute mouse coordinates for "follow" behavior
export type ChartTooltipPositioning = ParentPositioning | MousePositioning;
function isParentPositioning(positioning: ChartTooltipPositioning): positioning is ParentPositioning {
  return "positionParent" in positioning && (positioning.positionParent instanceof HTMLElement || positioning.positionParent === PositionParentType.ParentElement);
}
export interface ChartTooltipJsxProps {
  props?: ChartTooltipProps;
}
function ChartTooltipItem(props: {
  cssModule?: any;
  item: TooltipItem;
}) {
  const {
    print
  } = useTranslation();
  const item = props.item;
  if (item.type === TooltipItemType.Data) {
    return <TextHighlightReact className={styles.item}
    // key={key}
    text={print(item.text)} theme={item.theme ? item.theme : TEXT_HIGHLIGHT_THEME.NEUTRAL} />;
  }
  const itemTypeClassName = camelCase("section-".concat(item.type));
  return <span className={clsx(styles.item, styles[camelCase("section-".concat(item.type))], props.cssModule ? props.cssModule[itemTypeClassName] : "")} data-sentry-component="ChartTooltipItem" data-sentry-source-file="index.tsx">
      {print(item.text)}
    </span>;
}
const defaultProps = {
  sections: [],
  offset: 4,
  direction: Directions.Right,
  pointerEvents: "none" as const
};
export function ChartTooltip(inputProps: ChartTooltipProps) {
  var _props$cssModule;
  const props = _objectSpread(_objectSpread({}, defaultProps), inputProps);
  function getPositionParent(): HTMLElement | VirtualElement {
    const positioning = props.positioning;
    if (positioning && isParentPositioning(positioning)) {
      if (positioning.positionParent === PositionParentType.ParentElement) {
        // Note: this seems to be a typo that returns undefined. But drop-menu sets it to PositionParentType.ParentElement
        return props[positioning.positionParent];
      }
      return positioning.positionParent;
    }
    return {
      getBoundingClientRect: () => {
        const {
          clientX,
          clientY
        } = positioning as MousePositioning;
        return new DOMRect(clientX, clientY);
      }
    };
  }
  function getItemBackground(item) {
    const {
      bulletStyle,
      bulletColor
    } = item;
    return bulletStyle === TooltipBulletStyle.Solid ? {
      background: bulletColor
    } : {
      backgroundImage: "repeating-linear-gradient(-45deg, ".concat(bulletColor, ", ").concat(bulletColor, " 25%, ").concat(colors.gray0, " 25%, ").concat(colors.gray0, " 50%, ").concat(bulletColor, " 50%)"),
      backgroundSize: "4px 4px"
    };
  }
  const tooltipBody = <>
      <style>{css}</style>

      <div elref='chartTooltip' className={clsx(styles.body, (_props$cssModule = props.cssModule) === null || _props$cssModule === void 0 ? void 0 : _props$cssModule.body)}>
        {props.titleColor ? <div className={styles.colorIndicator} elref={"titleColorIndicator"} style={{
        backgroundColor: props.titleColor
      }} /> : null}
        <div className={styles.sections} elref={"sections"}>
          {props.sections.map((section, sectionIdx) => {
          var _props$cssModule2, _section$rows;
          return <div className={clsx(styles.sectionContainer, (_props$cssModule2 = props.cssModule) === null || _props$cssModule2 === void 0 ? void 0 : _props$cssModule2.sectionContainer)} key={sectionIdx}>
              {section.bulletColor ? <div className={styles.sectionColorIndicator} style={getItemBackground(section)} /> : null}
              {section.icon ? <SvgIconReact className={styles.sectionIcon} {...section.icon} /> : null}
              <div>
                {(_section$rows = section.rows) === null || _section$rows === void 0 ? void 0 : _section$rows.map((row, rowIdx) => <div className={styles.sectionRowContainer} key={rowIdx}>
                    {row.items.map((item, itemIdx) => <ChartTooltipItem cssModule={props.cssModule} item={item} key={itemIdx} />)}
                  </div>)}
              </div>
            </div>;
        })}
        </div>
        {props.children}
      </div>
      {props.footerText ? <div className={styles.footer} elref={"footer"}>
          <span>{props.footerText}</span>
        </div> : null}
    </>;
  if (props.positioning) {
    const dropMenuProps = {
      direction: props.direction,
      experimentalPositioning: true,
      open: true,
      positionParent: getPositionParent(),
      positionStrategy: PositioningStrategy.Fixed,
      preventStacking: true,
      style: {
        pointerEvents: props.pointerEvents
      }
    };
    if (props.renderInPortal) {
      return createPortal(<div role='tooltip'>
          <DropMenuReact {...dropMenuProps}>{tooltipBody}</DropMenuReact>
        </div>, window.document.querySelector("#tooltips") || document.body);
    }
    return <div className={clsx(styles.root, props.className)} role='tooltip'>
        <DropMenuReact {...dropMenuProps}> {tooltipBody} </DropMenuReact>
      </div>;
  }
  return <div className={clsx(styles.root, props.className)} role='tooltip' data-sentry-component="ChartTooltip" data-sentry-source-file="index.tsx">
      <div className={styles.wrapper}>{tooltipBody}</div>
    </div>;
}